import { useQueryClient } from "@tanstack/react-query";
import { Component, Suspense, lazy, useEffect } from "react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link, Navigate, useLocation } from "react-router";
import { diveSource } from "../api/dive-source.ts";
import { keys, useMeFeatures } from "../api/hooks.ts";
import { ME_KEY, useSession } from "../admin/session.ts";
import { loginUrl } from "../auth/next.ts";
import { DiverShell } from "../diver/DiverApp.tsx";
import { useLocale } from "../i18n/index.ts";
import type { DiveDataSource } from "../dive/source.ts";

// The dive view (three.js scene, tile streaming) is its own chunk, fetched only after the gate.
const DivePage = lazy(() => import("../dive/DivePage.tsx"));

/** Deliberately says nothing about why: no hint that a beta exists beyond "not available". */
function NotAvailable() {
  const { t } = useTranslation();
  const locale = useLocale();
  return (
    <DiverShell>
      <div className="grid gap-3 justify-items-start" data-testid="dive-unavailable">
        <h1 className="ds-text-title-1 m-0">{t("diveAccess.unavailableTitle")}</h1>
        <p className="ds-text-body m-0" role="status">
          {t("diveAccess.unavailable")}
        </p>
        <Link className="ds-btn ds-btn--tinted" to={`/${locale}/`}>
          {t("diveAccess.back")}
        </Link>
      </div>
    </DiverShell>
  );
}

/** The dive chunk could not be loaded (offline, or this tab is older than the deployed build). */
function ChunkFailed() {
  const { t } = useTranslation();
  return (
    <DiverShell>
      <div className="grid gap-3 justify-items-start" data-testid="dive-chunk-failed">
        <p className="ds-text-body m-0" role="alert">
          {t("diveAccess.chunkFailed")}
        </p>
        <button type="button" className="ds-btn ds-btn--tinted" onClick={() => location.reload()}>
          {t("diveAccess.reload")}
        </button>
      </div>
    </DiverShell>
  );
}

/** Catches a failed lazy import (and any render crash of the page) instead of the router's error page. */
class DiveBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  override state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  override render() {
    return this.state.failed ? <ChunkFailed /> : this.props.children;
  }
}

/** The access check itself failed (server, network, offline): not the same as "no access". */
function LoadFailed({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation();
  return (
    <DiverShell>
      <div className="grid gap-3 justify-items-start" data-testid="dive-load-failed">
        <p className="ds-text-body m-0" role="alert">
          {t("diveAccess.loadFailed")}
        </p>
        <button type="button" className="ds-btn ds-btn--tinted" onClick={onRetry}>
          {t("common.retry")}
        </button>
      </div>
    </DiverShell>
  );
}

/**
 * `/:lang/dive/:lake`: signed out goes to the login and comes back; signed in without dive
 * access sees "not available"; with access the lazily loaded dive view takes the whole screen.
 */
export function DiveRoute({
  lakeSlug,
  fromSpotSlug,
  source = diveSource,
}: {
  lakeSlug: string;
  fromSpotSlug?: string | undefined;
  source?: DiveDataSource;
}) {
  const { t } = useTranslation();
  const locale = useLocale();
  const location = useLocation();
  const session = useSession();
  const features = useMeFeatures(session.status === "user");
  const qc = useQueryClient();
  const staleSession = session.status === "user" && features.signedOut;
  useEffect(() => {
    // the server says "signed out" while /api/me is still cached as signed in: forget the cache
    if (staleSession) {
      qc.removeQueries({ queryKey: keys.meFeatures });
      void qc.invalidateQueries({ queryKey: ME_KEY });
    }
  }, [staleSession, qc]);

  if (session.status === "loading" || features.isPending) {
    return <p role="status">{t("common.loading")}</p>;
  }
  if (session.status === "anonymous" || staleSession) {
    return <Navigate to={loginUrl(locale, location.pathname + location.search)} replace />;
  }
  if (features.isError) return <LoadFailed onRetry={features.refetch} />;
  if (!features.dive) return <NotAvailable />;
  return (
    <DiveBoundary>
      <Suspense fallback={<p role="status">{t("common.loading")}</p>}>
        <DivePage lakeSlug={lakeSlug} source={source} {...(fromSpotSlug ? { fromSpotSlug } : {})} />
      </Suspense>
    </DiveBoundary>
  );
}
